import React from 'react';
import { useTranslation } from 'react-i18next';
import './action-buttons.scss';

// -------------------------------------------------------------
// 1. Confirmation Buttons (e.g., Update vs. That is correct)
// -------------------------------------------------------------
interface ConfirmationButtonsProps {
  onUpdate: () => void;
  onCorrect: () => void;
  onlyUpdate?: boolean;
  disabledCorrect?: boolean;
  disabledUpdate?: boolean;
}

export const ConfirmationButtons: React.FC<ConfirmationButtonsProps> = ({ 
  onUpdate, 
  onCorrect, 
  onlyUpdate, 
  disabledCorrect,
  disabledUpdate 
}) => {
  const { t } = useTranslation();

  return (
    <div className="v2-actions v2-actions--confirm">
      <button className="v2-btn v2-btn--secondary" onClick={onUpdate} disabled={disabledUpdate}>
        {t('v2Update')}
      </button>
      {!onlyUpdate && (
        <button className="v2-btn v2-btn--primary" onClick={onCorrect} disabled={disabledCorrect}>
          {t('v2ThatIsCorrect')}
        </button>
      )}
    </div>
  );
};

// -------------------------------------------------------------
// 2. Streak Challenge Popup Bubble
// -------------------------------------------------------------
interface StreakChallengeProps {
  onSelectOption: (days: number | null) => void;
  selectedOption: number | null | undefined;
  disabled?: boolean;
}

export const StreakChallengePopup: React.FC<StreakChallengeProps> = ({ onSelectOption, selectedOption, disabled }) => {
  const { t } = useTranslation();

  return (
    <div className="v2-streak-popup">
      <div className="v2-streak-popup__header">
        <h3>🔥 {t('v2StreakChallenge')}</h3>
        <p>{t('v2StreakSubtext')}</p>
      </div>
      <div className="v2-streak-popup__actions">
        <button 
          className={`v2-btn v2-btn--small ${selectedOption === null ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
          onClick={() => !disabled && onSelectOption(null)}
          disabled={disabled}
        >
          {t('v2NotRightNow')}
        </button>
        <button 
          className={`v2-btn v2-btn--small ${selectedOption === 7 ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
          onClick={() => !disabled && onSelectOption(7)}
          disabled={disabled}
        >
          {t('v2Days7')}
        </button>
        <button 
          className={`v2-btn v2-btn--small ${selectedOption === 10 ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
          onClick={() => !disabled && onSelectOption(10)}
          disabled={disabled}
        >
          {t('v2Days10')}
        </button>
        <button 
          className={`v2-btn v2-btn--small ${selectedOption === 15 ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
          onClick={() => !disabled && onSelectOption(15)}
          disabled={disabled}
        >
          {t('v2Days15')}
        </button>
      </div>
    </div>
  );
};

import { 
  studyIcon, 
  exerciseIcon, 
  homeworkIcon, 
  examIcon, 
  Action,
  Close
} from '../../../assets/images/index';

// -------------------------------------------------------------
// 3. Study Mode Selector (Horizontal Buttons for Chat)
// -------------------------------------------------------------
type StudyMode = 'exam' | 'homework' | 'exercise' | 'study';

interface StudyModeSelectorProps {
  onSelectMode: (mode: StudyMode) => void;
  selectedMode: StudyMode | null;
}

export const StudyModeSelector: React.FC<StudyModeSelectorProps> = ({ onSelectMode, selectedMode }) => {
  const { t } = useTranslation();

  return (
    <div className="v2-actions v2-actions--modes">
      <button 
        className={`v2-btn v2-btn--compact ${selectedMode === 'study' ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
        onClick={() => onSelectMode('study')}
      >
        {t('v2ModeStudy')}
      </button>
      <button 
        className={`v2-btn v2-btn--compact ${selectedMode === 'exercise' ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
        onClick={() => onSelectMode('exercise')}
      >
        {t('v2ModeExercise')}
      </button>
      <button 
        className={`v2-btn v2-btn--compact ${selectedMode === 'homework' ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
        onClick={() => onSelectMode('homework')}
      >
        {t('v2ModeHomework')}
      </button>
      <button 
        className={`v2-btn v2-btn--compact ${selectedMode === 'exam' ? 'v2-btn--selected' : 'v2-btn--outline'}`} 
        onClick={() => onSelectMode('exam')}
      >
        {t('v2ModeExam')}
      </button>
    </div>
  );
};

// -------------------------------------------------------------
// 4. Study Mode Modal (Vertical List from Figma)
// -------------------------------------------------------------
interface StudyModeModalProps {
  onSelectMode: (mode: StudyMode) => void;
  selectedMode: StudyMode | null;
  onClose?: () => void;
}

export const StudyModeModal: React.FC<StudyModeModalProps> = ({ onSelectMode, selectedMode, onClose }) => {
  const { t } = useTranslation();

  const modes: { id: StudyMode; label: string; icon: string }[] = [
    { id: 'study', label: t('v2ModeStudy'), icon: studyIcon },
    { id: 'exercise', label: t('v2ModeExercise'), icon: exerciseIcon },
    { id: 'homework', label: t('v2ModeHomework'), icon: homeworkIcon },
    { id: 'exam', label: t('v2ModeExam'), icon: examIcon },
  ];

  return (
    <div className="v2-actions--modal-overlay" onClick={onClose}>
      <div className="v2-actions v2-actions--vertical" onClick={(e) => e.stopPropagation()}>
        <div className="v2-actions--modes__title">
            <img 
              src={Close} 
              alt="close" 
              className="v2-close-icon" 
              onClick={onClose}
              style={{ cursor: 'pointer' }}
            />
            <h3>{t('v2SelectContext')}</h3>
        </div>

        {modes.map((mode) => (
            <div 
            key={mode.id}
            className={`v2-mode-item ${selectedMode === mode.id ? 'v2-mode-item--selected' : ''}`}
            onClick={() => onSelectMode(mode.id)}
            >
            <div className="v2-mode-icon-wrapper">
                <img src={mode.icon} alt={mode.id} />
            </div>
            <span>{mode.label}</span>
            </div>
        ))}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 5. Lesson Interaction Button
// -------------------------------------------------------------
import LessonBtn from '../../button';
import { useLayoutContext } from '../../../layouts/general-layout';

interface LessonActionBtnProps {
  onClick: () => void;
  text?: string;
  disabled?: boolean;
  variant?: 'contained' | 'outlined';
  color?: "blue" | "green" | "red" | "black" | "orange" | "green-small" | "lightBlue" | "transparent" | "purple" | "success" | "dark";
  className?: string;
  icon?: React.ReactNode | string;
  iconRight?: React.ReactNode | string;
}

export const LessonActionBtn: React.FC<LessonActionBtnProps> = ({ 
  onClick, 
  text, 
  disabled, 
  variant = 'contained',
  color = 'dark',
  className = "",
  icon,
  iconRight
}) => {
  const { t } = useLayoutContext();
  const btnText = text || t('watchedAndUnderstood');

  const renderedIcon = typeof icon === 'string' ? <img src={icon} alt="" className="btn-icon-left-img" /> : icon;
  const renderedIconRight = typeof iconRight === 'string' ? <img src={iconRight} alt="" className="btn-icon-right-img" /> : iconRight;

  return (
    <LessonBtn 
      text={btnText} 
      onClick={onClick} 
      color={color} 
      outline={variant === 'outlined'}
      disabled={disabled}
      className={`v2-lesson-action-btn ${className}`}
      icon={renderedIcon}
      iconRight={renderedIconRight}
    />
  );
};
